import { Activity as ActivityIcon } from "lucide-react";
import type { Trip } from "../lib/api.ts";
import { useApi } from "../lib/useApi.ts";
import { Card, EmptyState, PageState } from "../ui/index.ts";

export type ActivityItem = {
  id: string;
  action: string;
  actorName: string | null;
  subjectIds?: Record<string, string>;
  at: string;
};

const PHRASE: Record<string, string> = {
  "trip.create": "created the trip",
  "trip.start": "started the trip",
  "trip.end": "ended capture",
  "invitation.redeem": "joined the trip",
  "asset.submit": "added footage",
  "tag.create": "made a tag",
  "tag.delete": "deleted a tag",
  "deliverable.create": "planned a deliverable",
  "revision.publish": "uploaded a new version",
  "review.decide": "reviewed a version",
  "deliverable.deliver": "delivered a reel",
};

export const describeActivity = (a: ActivityItem) =>
  `${a.actorName ?? "Someone"} ${PHRASE[a.action] ?? "made a change"}`;

export function ActivityList({ items }: { items: ActivityItem[] }) {
  return (
    <ul className="plain">
      {items.map((a) => (
        <li key={a.id} className="row-item">
          <span className="row-main">
            <span>{describeActivity(a)}</span>
            <span className="row-detail">
              <time dateTime={a.at}>{new Date(a.at).toLocaleString()}</time>
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ActivityPage({ trip }: { trip: Trip }) {
  const feed = useApi<{ items: ActivityItem[] }>(`/v1/trips/${trip.id}/activity?limit=100`, {
    pollMs: 30_000,
  });
  return (
    <PageState
      {...feed}
      isEmpty={(d) => d.items.length === 0}
      empty={
        <EmptyState
          icon={<ActivityIcon size={24} strokeWidth={1.5} />}
          title="Nothing has happened yet"
          body="Joins, new footage, ideas and reviews will show up here."
        />
      }
    >
      {(d) => (
        <Card>
          <ActivityList items={d.items} />
        </Card>
      )}
    </PageState>
  );
}
